import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Copy, GitMerge, Plus, Search, Sparkles, Variable } from 'lucide-react';
import { DialogShell, ghostBtn, primaryBtn } from '../components/dataModelsUi';
import { logDataModels } from '../dataModelsLogger';
import { examplesForType, type ExpressionExample } from './expressionExamples';
import type { PolicyType } from './types';
import { POLICY_TYPE_LABEL } from './types';
import { ALL_VARIABLES, FUNCTION_REGISTRY, KEYWORDS, REQUEST_VARIABLES, RESOURCE_VARIABLES, USER_VARIABLES } from './variables';

type HelpPane = 'variables' | 'examples' | 'conflicts' | 'copilot';

const PANES: Array<{ id: HelpPane; label: string; hint: string; soon?: boolean; icon: typeof Variable }> = [
  { id: 'variables', label: 'Variables', hint: '${user.*} tokens', icon: Variable },
  { id: 'examples', label: 'Examples', hint: 'Copy or insert', icon: BookOpen },
  { id: 'conflicts', label: 'Conflicts', hint: 'Who wins', icon: GitMerge },
  { id: 'copilot', label: 'Copilot', hint: 'Coming soon', soon: true, icon: Sparkles },
];

export default function ExpressionHelpDialog({
  isLight,
  policyType,
  onClose,
  onInsert,
  onCopy,
  canInsert = true,
}: {
  isLight: boolean;
  policyType: PolicyType;
  onClose: () => void;
  onInsert: (expression: string) => void;
  onCopy: (value: string) => void;
  canInsert?: boolean;
}) {
  const [pane, setPane] = useState<HelpPane>('variables');
  const [query, setQuery] = useState('');
  const examples = useMemo(() => examplesForType(policyType), [policyType]);
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const rail = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';

  useEffect(() => {
    logDataModels('app', 'governance_expression_help_tab', { pane, policyType });
  }, [pane, policyType]);

  return (
    <DialogShell
      title="Policy help"
      subtitle="Variables, examples, and conflict rules. Copilot will draft expressions here later."
      isLight={isLight}
      onClose={onClose}
      size="large"
      footer={<button type="button" className={primaryBtn()} onClick={onClose}>Done</button>}
    >
      <div className="flex h-[min(560px,calc(100vh-14rem))] min-h-[28rem]">
        <nav className={`flex w-[208px] shrink-0 flex-col border-r p-2 ${rail}`}>
          <p className={`px-2 pb-2 pt-1 text-[10px] font-semibold uppercase tracking-wide ${muted}`}>Browse</p>
          {PANES.map((item) => {
            const Icon = item.icon;
            const active = pane === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setPane(item.id)}
                className={`mb-0.5 flex w-full items-start gap-2 rounded-md px-2 py-2 text-left ${
                  active
                    ? isLight ? 'bg-white text-[#111827] shadow-sm' : 'bg-white/10 text-white'
                    : muted
                }`}
              >
                <Icon size={14} className="mt-0.5 shrink-0" />
                <span className="min-w-0">
                  <span className={`flex items-center gap-1.5 text-[12px] ${active ? 'font-medium' : ''}`}>
                    {item.label}
                    {item.soon ? (
                      <span className={`rounded-full px-1.5 py-px text-[9px] font-medium uppercase ${
                        isLight ? 'bg-[#EEF2FF] text-[#3730A3]' : 'bg-indigo-400/15 text-indigo-200'
                      }`}
                      >
                        Soon
                      </span>
                    ) : null}
                  </span>
                  <span className={`mt-0.5 block text-[11px] ${muted}`}>{item.hint}</span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1 overflow-y-auto px-4 py-4">
          {pane === 'variables' ? (
            <VariablesHelp isLight={isLight} query={query} onQuery={setQuery} onCopy={onCopy} ink={ink} muted={muted} />
          ) : null}
          {pane === 'examples' ? (
            <div className="space-y-2.5">
              <p className={`text-[12px] ${muted}`}>Examples for {POLICY_TYPE_LABEL[policyType].replace(' Policy', '').toLowerCase()} first. Insert writes into the expression.</p>
              {examples.map((example) => (
                <ExampleCard key={example.id} example={example} currentType={policyType} isLight={isLight} onCopy={onCopy} onInsert={onInsert} canInsert={canInsert} />
              ))}
            </div>
          ) : null}
          {pane === 'conflicts' ? <ConflictsHelp isLight={isLight} /> : null}
          {pane === 'copilot' ? <CopilotHelp isLight={isLight} /> : null}
        </div>
      </div>
    </DialogShell>
  );
}

function VariablesHelp({
  isLight,
  query,
  onQuery,
  onCopy,
  ink,
  muted,
}: {
  isLight: boolean;
  query: string;
  onQuery: (value: string) => void;
  onCopy: (value: string) => void;
  ink: string;
  muted: string;
}) {
  const needle = query.trim().toLowerCase();
  const match = (text: string) => !needle || text.toLowerCase().includes(needle);
  const users = USER_VARIABLES.filter((item) => match(item.path) || match(item.description));
  const requests = REQUEST_VARIABLES.filter((item) => match(item.path) || match(item.description));
  const resources = RESOURCE_VARIABLES.filter((item) => match(item.path) || match(item.description));
  const functions = FUNCTION_REGISTRY.filter((item) => match(item.name) || match(item.signature) || match(item.description));
  const empty = !users.length && !requests.length && !resources.length && !functions.length;
  const field = isLight
    ? 'border-[#D8DCE2] bg-white text-[#1C1C1C] placeholder:text-[#9AA3AF]'
    : 'border-white/15 bg-black text-slate-100 placeholder:text-slate-500';

  return (
    <div className="space-y-4">
      <label className={`flex h-8 items-center gap-2 rounded-md border px-2 ${field}`}>
        <Search size={13} className={muted} />
        <input
          className="h-full min-w-0 flex-1 bg-transparent text-[12px] outline-none"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          placeholder="Search variables and functions"
          aria-label="Search variables and functions"
        />
      </label>
      {empty ? <p className={`text-[12px] ${muted}`}>No matches for “{query}”.</p> : null}
      {users.length ? <VariableGroup title="User" items={users} isLight={isLight} onCopy={onCopy} /> : null}
      {requests.length ? <VariableGroup title="Request" items={requests} isLight={isLight} onCopy={onCopy} /> : null}
      {resources.length ? <VariableGroup title="Resource" items={resources} isLight={isLight} onCopy={onCopy} /> : null}
      {functions.length ? (
        <div>
          <p className={`mb-2 text-[12px] font-semibold ${ink}`}>Functions</p>
          <div className="space-y-1.5">
            {functions.map((item) => (
              <HelpRow
                key={item.name}
                isLight={isLight}
                title={item.signature}
                detail={item.description}
                onCopy={() => onCopy(item.name.startsWith('CURRENT') ? item.name : `${item.name}()`)}
              />
            ))}
          </div>
        </div>
      ) : null}
      <p className={`text-[11px] ${muted}`}>Keywords: {KEYWORDS.join(', ')}. Type $ in the editor for IntelliSense. {ALL_VARIABLES.length} variables available.</p>
    </div>
  );
}

function VariableGroup({
  title,
  items,
  isLight,
  onCopy,
}: {
  title: string;
  items: typeof USER_VARIABLES;
  isLight: boolean;
  onCopy: (value: string) => void;
}) {
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  return (
    <div>
      <p className={`mb-2 text-[12px] font-semibold ${ink}`}>{title}</p>
      <div className="space-y-1.5">
        {items.map((item) => {
          const token = `\${${item.path}}`;
          return (
            <HelpRow
              key={item.path}
              isLight={isLight}
              title={token}
              detail={`${item.type} · ${item.description}`}
              example={item.example}
              onCopy={() => onCopy(token)}
            />
          );
        })}
      </div>
    </div>
  );
}

function HelpRow({
  isLight,
  title,
  detail,
  example,
  onCopy,
}: {
  isLight: boolean;
  title: string;
  detail: string;
  example?: string;
  onCopy: () => void;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const card = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';
  return (
    <div className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${card}`}>
      <div className="min-w-0">
        <p className={`font-mono text-[12px] ${ink}`}>{title}</p>
        <p className={`mt-0.5 text-[11px] ${muted}`}>{detail}</p>
        {example ? <p className={`mt-0.5 font-mono text-[11px] ${muted}`}>e.g. {example}</p> : null}
      </div>
      <button type="button" className={ghostBtn(isLight)} onClick={onCopy}>
        <Copy size={12} />
        Copy
      </button>
    </div>
  );
}

function ExampleCard({
  example,
  currentType,
  isLight,
  onCopy,
  onInsert,
  canInsert,
}: {
  example: ExpressionExample;
  currentType: PolicyType;
  isLight: boolean;
  onCopy: (value: string) => void;
  onInsert: (expression: string) => void;
  canInsert: boolean;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const card = isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-black';
  return (
    <article className={`rounded-lg border px-3 py-3 ${card}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <p className={`text-[13px] font-semibold ${ink}`}>{example.title}</p>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>
          {POLICY_TYPE_LABEL[example.type].replace(' Policy', '')}
        </span>
        {example.type === currentType ? <span className={`text-[10px] ${muted}`}>For this policy</span> : null}
      </div>
      <p className={`text-[12px] ${muted}`}>{example.reason}</p>
      <pre className={`mt-2 overflow-x-auto rounded-md px-2 py-2 font-mono text-[11px] leading-5 ${isLight ? 'bg-[#F7F8FA] text-[#1C1C1C]' : 'bg-white/5 text-slate-100'}`}>{example.expression}</pre>
      <div className="mt-2 flex gap-2">
        <button type="button" className={ghostBtn(isLight)} onClick={() => onCopy(example.expression)}>
          <Copy size={12} />
          Copy
        </button>
        {canInsert ? (
          <button type="button" className={ghostBtn(isLight)} onClick={() => onInsert(example.expression)}>
            <Plus size={12} />
            Insert
          </button>
        ) : null}
      </div>
    </article>
  );
}

const CONFLICT_RULES: Array<{ clash: string; winner: string }> = [
  { clash: 'Deny vs mask', winner: 'Deny. The column is blocked, not starred.' },
  { clash: 'Full mask vs partial', winner: 'Full hide or hash.' },
  { clash: 'Two row filters', winner: 'Both. They combine with AND.' },
  { clash: 'Parent deny vs child allow', winner: 'Parent. A child cannot loosen inherited deny.' },
  { clash: 'Access deny vs anything else', winner: 'The whole statement is blocked.' },
];

function ConflictsHelp({ isLight }: { isLight: boolean }) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const card = isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]';
  return (
    <div className="space-y-3">
      <p className={`text-[13px] leading-5 ${ink}`}>
        More than one policy may match the same column. Only one effect runs. Deny beats mask. Full hide beats partial.
      </p>
      <div className={`overflow-hidden rounded-md border ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
        <table className="min-w-full text-[12px]">
          <thead>
            <tr className={`text-left ${isLight ? 'bg-[#F7F8FA] text-[#5C6570]' : 'bg-white/[0.04] text-slate-400'}`}>
              <th className="px-3 py-2 font-medium">Clash</th>
              <th className="px-3 py-2 font-medium">Winner</th>
            </tr>
          </thead>
          <tbody>
            {CONFLICT_RULES.map((rule) => (
              <tr key={rule.clash} className={`border-t ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
                <td className={`px-3 py-2 font-medium ${ink}`}>{rule.clash}</td>
                <td className={`px-3 py-2 ${muted}`}>{rule.winner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={`rounded-md border px-3 py-2.5 ${card}`}>
        <p className={`text-[12px] leading-5 ${muted}`}>
          Different audiences are not a clash. External hide and HR clear are two Who rules, not two winners.
        </p>
        <p className={`mt-1.5 text-[12px] leading-5 ${muted}`}>
          Use Sensitive for a label such as PII. Use Column for a named field only when it is stricter, such as deny national_id.
        </p>
        <p className={`mt-1.5 text-[12px] leading-5 ${muted}`}>
          At run time the query engine applies one rewrite. It does not stack two CASE expressions on the same column.
        </p>
      </div>
    </div>
  );
}

function CopilotHelp({ isLight }: { isLight: boolean }) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const box = isLight
    ? 'border-[#D8DCE2] bg-[#F7F8FA] text-[#9AA3AF]'
    : 'border-white/15 bg-white/[0.03] text-slate-500';
  return (
    <div className="flex h-full min-h-[22rem] flex-col items-center justify-center px-6 text-center">
      <span className={`mb-3 flex h-10 w-10 items-center justify-center rounded-full ${isLight ? 'bg-[#EEF2FF] text-[#3730A3]' : 'bg-indigo-400/15 text-indigo-200'}`}>
        <Sparkles size={18} />
      </span>
      <p className={`text-[15px] font-semibold ${ink}`}>Ask Copilot</p>
      <p className={`mt-1 max-w-sm text-[12px] leading-5 ${muted}`}>
        Describe the rule in plain English. Copilot will draft the expression and sit beside Variables and Conflicts. The assistant is not wired yet.
      </p>
      <textarea
        disabled
        rows={3}
        placeholder="e.g. Hide salary from everyone except HR in the EU"
        className={`mt-4 w-full max-w-md resize-none rounded-md border px-3 py-2 text-[12px] ${box}`}
      />
    </div>
  );
}
